import {
	ArrowDownOutlined,
	ArrowUpOutlined,
	DeleteOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import { FORM_FIELD_TYPES } from "@domain/form";
import { Button, Dropdown, Empty, List, Space, Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import type { FormField, FormFieldType } from "../types";
import { ONLINE_ONLY_TYPES } from "../types";

interface Props {
	fields: FormField[];
	selectedKey: string | null;
	onSelect: (key: string) => void;
	onAdd: (type: FormFieldType) => void;
	onRemove: (key: string) => void;
	/** `delta` — `-1` yuqoriga, `+1` pastga. */
	onMove: (key: string, delta: -1 | 1) => void;
	/** Maydon kaliti → shu maydondagi validatsiya xatolari (Task 7). */
	errors?: Map<string, string[]>;
}

/**
 * ⚠️ Tartib `↑` / `↓` tugmalari bilan o'zgaradi, drag-drop bilan EMAS —
 * loyihaga DnD kutubxonasi qo'shilmaydi (Task 5 sarlavhasidagi izoh).
 *
 * ⚠️ Tartib **mantiqiy ahamiyatga ega**: shart faqat o'zidan OLDIN
 * kelgan maydonga havola qila oladi (spec §4.3 invariant 1). Maydonni
 * yuqoriga surish uning shartini buzishi mumkin — buzilgan havola
 * `validateFormSchema` ning `forward_reference` xatosi bilan Task 7 da
 * ko'rsatiladi.
 */
export function FieldListPanel({
	fields,
	selectedKey,
	onSelect,
	onAdd,
	onRemove,
	onMove,
	errors,
}: Props) {
	const { t } = useTranslation("leadForms");
	const typeLabel = (type: FormFieldType) => t(`fieldType.${type}`);

	return (
		<div className="flex h-full flex-col gap-3">
			<Dropdown
				trigger={["click"]}
				menu={{
					items: FORM_FIELD_TYPES.map((type) => ({
						key: type,
						label: typeLabel(type),
					})),
					onClick: (info) => onAdd(info.key as FormFieldType),
				}}
			>
				<Button type="primary" icon={<PlusOutlined />} block>
					{t("fieldList.addField")}
				</Button>
			</Dropdown>

			{fields.length === 0 ? (
				<Empty description={t("fieldList.empty")} />
			) : (
				<List
					size="small"
					bordered
					dataSource={fields}
					rowKey={(field) => field.key}
					renderItem={(field, index) => (
						<List.Item
							className={field.key === selectedKey ? "bg-blue-50" : undefined}
							onClick={() => onSelect(field.key)}
							actions={[
								<Button
									key="up"
									type="text"
									size="small"
									icon={<ArrowUpOutlined />}
									title={t("fieldList.moveUp")}
									disabled={index === 0}
									onClick={(event) => {
										event.stopPropagation();
										onMove(field.key, -1);
									}}
								/>,
								<Button
									key="down"
									type="text"
									size="small"
									icon={<ArrowDownOutlined />}
									title={t("fieldList.moveDown")}
									disabled={index === fields.length - 1}
									onClick={(event) => {
										event.stopPropagation();
										onMove(field.key, 1);
									}}
								/>,
								<Button
									key="remove"
									type="text"
									size="small"
									danger
									icon={<DeleteOutlined />}
									title={t("fieldList.remove")}
									onClick={(event) => {
										event.stopPropagation();
										onRemove(field.key);
									}}
								/>,
							]}
						>
							<Space direction="vertical" size={0}>
								<Space size={4}>
									<span>{field.label}</span>
									{field.required && <Tag color="red">{t("fieldList.requiredTag")}</Tag>}
									{ONLINE_ONLY_TYPES.has(field.type) && (
										<Tooltip title={t("fieldList.offlineOnlyTooltip")}>
											<Tag color="orange">{t("fieldList.offlineOnlyTag")}</Tag>
										</Tooltip>
									)}
									{field.visibleIf && field.visibleIf.length > 0 && (
										<Tag>{t("fieldList.conditionalTag")}</Tag>
									)}
									{(errors?.get(field.key)?.length ?? 0) > 0 && (
										<Tooltip title={errors?.get(field.key)?.join(" · ")}>
											<Tag color="error">{t("fieldList.errorTag")}</Tag>
										</Tooltip>
									)}
								</Space>
								<span className="text-xs text-gray-500">
									{field.key} · {typeLabel(field.type)}
								</span>
							</Space>
						</List.Item>
					)}
				/>
			)}
		</div>
	);
}
